import { useCallback, useEffect } from "react";

import { useObjectMemo } from "byzantine";
import { useBreakpoints } from "@narmi/design_system";

import { useCurrentUser } from "../../../components/contexts/CurrentUserContext";

import { useSidebarStorage, sidebarActions } from "./storage";

import type { SidebarTileId } from "./sidebar.constants";

/**
 * Will not render the sidebar within a grid on mobile devices
 */
export const useIsSidebarEnabled = () => {
  const { m } = useBreakpoints();
  const { currentUser } = useCurrentUser();
  return {
    isSidebarEnabled: currentUser !== null && m,
  };
};

/**
 * Manages sidebar collapsed state with localStorage persistence.
 * State is reactive and persists across page navigations and browser sessions.
 * Each user gets isolated sidebar state based on their user ID.
 *
 * The state is stored in localStorage under the key "sidebar_{hashedUserId}"
 * and automatically syncs across browser tabs/windows.
 */
export const useSidebarContainer = () => {
  const { currentUser } = useCurrentUser();
  const { isSidebarEnabled } = useIsSidebarEnabled();

  const userId = currentUser?.id?.toString() || "";
  const [sidebarState, setSidebarState] = useSidebarStorage(userId);

  /**
   * Sync localStorage state with DOM class for Django template styling.
   * See: indigo/templates/indigo/banking.html
   * This runs on mount and whenever isCollapsed changes.
   */
  useEffect(() => {
    if (isSidebarEnabled) {
      const sidebarDiv = document.getElementById("sidebar-layout");
      if (sidebarDiv) {
        sidebarDiv.className = sidebarState.isCollapsed
          ? "sidebar-layout--collapsed"
          : "sidebar-layout";
      }
    }
  }, [sidebarState.isCollapsed, isSidebarEnabled]);

  /**
   * Toggles the sidebar collapsed state.
   * When collapsing, saves the currently expanded menu groups and collapses them.
   * When expanding, restores the previously expanded menu groups.
   * This updates both localStorage and the DOM element class.
   */
  const toggleCollapseClass = useCallback(() => {
    if (sidebarState.isCollapsed) {
      // Expanding: restore saved expanded menu groups
      setSidebarState((prev) => ({
        ...prev,
        isCollapsed: false,
        expandedMenuIds: prev.savedExpandedMenuIds || [],
        savedExpandedMenuIds: [],
      }));
    } else {
      // Collapsing: save current expanded menu groups, then collapse all
      setSidebarState((prev) => ({
        ...prev,
        isCollapsed: true,
        savedExpandedMenuIds: prev.expandedMenuIds,
        expandedMenuIds: [],
      }));
    }
  }, [sidebarState.isCollapsed, setSidebarState]);

  /**
   * Dismiss a tile by its ID
   */
  const dismissTile = useCallback(
    (tileId: SidebarTileId) => {
      setSidebarState((prev) => sidebarActions.dismissTile(prev, tileId));
    },
    [setSidebarState]
  );

  /**
   * Check if a tile is dismissed
   */
  const isTileDismissed = useCallback(
    (tileId: SidebarTileId) => {
      return sidebarActions.isTileDismissed(sidebarState, tileId);
    },
    [sidebarState]
  );

  /**
   * Get the timestamp when a tile was dismissed
   */
  const getTileDismissedAt = useCallback(
    (tileId: SidebarTileId) => {
      return sidebarActions.getTileDismissedAt(sidebarState, tileId);
    },
    [sidebarState]
  );

  /**
   * Toggle a menu group's expanded state
   */
  const toggleMenuGroup = useCallback(
    (menuId: string) => {
      setSidebarState((prev) => sidebarActions.toggleMenuGroup(prev, menuId));
    },
    [setSidebarState]
  );

  /**
   * Check if a menu group is expanded
   */
  const isMenuGroupExpanded = useCallback(
    (menuId: string) => {
      return (
        !sidebarState.isCollapsed &&
        sidebarActions.isMenuGroupExpanded(sidebarState, menuId)
      );
    },
    [sidebarState]
  );

  return useObjectMemo({
    isCollapsed: sidebarState.isCollapsed,
    dismissedTileIds: sidebarState.dismissedTileIds,
    expandedMenuIds: sidebarState.expandedMenuIds,
    toggleCollapseClass,
    dismissTile,
    isTileDismissed,
    getTileDismissedAt,
    toggleMenuGroup,
    isMenuGroupExpanded,
  });
};
